'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
      return twMerge(clsx(inputs));
}

interface Brand {
      brandLogo: {
            filename_disk: string;
            title: string;
      };
}

// --- LOGO KUTUSU BİLEŞENİ ---
const LogoBox = ({
      brand,
      className,
      delay = 0,
      bgColor = "bg-white"
}: {
      brand: Brand | null,
      className?: string,
      delay?: number,
      bgColor?: string
}) => {
      return (
            <motion.div
                  animate={{ y: [-6, 6, -6] }}
                  transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: delay
                  }}
                  className={cn(
                        "absolute flex items-center justify-center overflow-hidden border border-white/60 backdrop-blur-md shadow-[0_8px_30px_rgb(0,0,0,0.04)]",
                        bgColor,
                        "rounded-4xl", // rounded-[2rem] yerine standart sınıf
                        className
                  )}
            >
                  <AnimatePresence mode="wait">
                        {brand && brand?.brandLogo?.filename_disk && (
                              <motion.div
                                    key={brand.brandLogo.filename_disk}
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.8, opacity: 0 }}
                                    transition={{ duration: 0.5, ease: "easeOut" }}
                                    className="absolute inset-0 flex items-center justify-center p-3"
                              >
                                    <div className="relative w-full h-full">
                                          <Image
                                                src={`https://api.gynoclub.com.tr/assets/${brand.brandLogo.filename_disk}`}
                                                alt={brand.brandLogo.title}
                                                fill
                                                className="object-contain"
                                                sizes="(max-width: 768px) 80px, 160px"
                                          />
                                    </div>
                              </motion.div>
                        )}
                  </AnimatePresence>
            </motion.div>
      );
};

// --- EXPAND BUTTON (DIŞARI ALINDI) ---
const ExpandButton = ({
      className,
      isExpanded,
      onClick
}: {
      className?: string,
      isExpanded: boolean,
      onClick: () => void
}) => (
      <button
            onClick={onClick}
            className={cn(
                  "group flex items-center gap-3 text-gray-900 font-semibold text-lg hover:text-[#6320EE] transition-colors",
                  className
            )}
      >
            <span>{isExpanded ? 'Listeyi Gizle' : 'Tüm Markaları Gör'}</span>
            <div className={cn(
                  "w-10 h-10 rounded-full bg-white border border-gray-200 group-hover:bg-[#6320EE] group-hover:border-[#6320EE] group-hover:text-white flex items-center justify-center transition-all duration-300 shadow-sm",
                  isExpanded ? "rotate-180" : ""
            )}>
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
      </button>
);

const BrandIntegrationSection = () => {
      const [brands, setBrands] = useState<Brand[]>([]);
      const [activeIndices, setActiveIndices] = useState<number[]>([-1, -1, -1, -1, -1]);
      const [isExpanded, setIsExpanded] = useState(false);

      const activeIndicesRef = useRef<number[]>([-1, -1, -1, -1, -1]);
      const brandsRef = useRef<Brand[]>([]);

      useEffect(() => {
            const fetchBrands = async () => {
                  try {
                        const res = await fetch('https://api.gynoclub.com.tr/items/brandList?fields=brandTitle, brandLogo.filename_disk, brandLogo.title');
                        const json = await res.json();
                        if (json.data && json.data.length > 0) {
                              setBrands(json.data);
                              brandsRef.current = json.data;
                              const initialIndices: number[] = [];
                              while (initialIndices.length < 5) {
                                    const r = Math.floor(Math.random() * json.data.length);
                                    if (!initialIndices.includes(r)) initialIndices.push(r);
                              }
                              setActiveIndices(initialIndices);
                              activeIndicesRef.current = initialIndices;
                        }
                  } catch (err) {
                        console.error(err);
                  }
            };
            fetchBrands();
      }, []);

      useEffect(() => {
            if (brands.length === 0) return;

            const timers = [3500, 4800, 5200, 4100, 6500].map((delay, boxIndex) => {
                  return setInterval(() => {
                        let newIndex = -1;
                        let attempts = 0;
                        const totalBrands = brandsRef.current.length;

                        do {
                              newIndex = Math.floor(Math.random() * totalBrands);
                              attempts++;
                        } while (activeIndicesRef.current.includes(newIndex) && attempts < 20);

                        setActiveIndices(prev => {
                              const newState = [...prev];
                              newState[boxIndex] = newIndex;
                              activeIndicesRef.current = newState;
                              return newState;
                        });

                  }, delay);
            });

            return () => timers.forEach(clearInterval);
      }, [brands]);

      if (brands.length === 0) return null;

      const firstRow = brands.slice(0, Math.ceil(brands.length / 2));
      const secondRow = brands.slice(Math.ceil(brands.length / 2));

      return (
            <section className="relative w-full py-20 lg:py-24 bg-[#FAFAFA] overflow-hidden transition-all duration-500">

                  <div className="absolute right-0 top-0 w-full lg:w-2/3 h-full bg-linear-to-l from-purple-50/40 to-transparent pointer-events-none" />

                  <div className="container mx-auto px-6 relative z-10">
                        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">


                              <div className="w-full lg:w-1/2 flex flex-col items-start z-20">


                                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                                          En Seçkin Markalar <br />
                                          <span className="text-transparent bg-clip-text bg-linear-to-r from-[#6320EE] to-[#DA38E2]">
                                                Gyno Club&rsquo;da Sizinle
                                          </span>
                                    </h2>

                                    <p className="text-gray-600 text-lg leading-relaxed mb-8 max-w-xl">
                                          Giyimden kozmetiğe, yeme içmeden sağlığa kadar yüzlerce premium marka ile yaptığımız özel anlaşmalar sayesinde, ayrıcalıklı dünyanın keyfini çıkarın.
                                    </p>


                                    <div className="hidden lg:block">
                                          <ExpandButton isExpanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />
                                    </div>
                              </div>


                              <div className="w-full lg:w-1/2 relative flex flex-col items-center">

                                    <div className="relative w-full h-112.5 lg:h-150 flex items-center justify-center">

                                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-75 h-75 bg-pink-100/60 blur-[80px] rounded-full -z-10" />

                                          <div className="relative w-full h-full max-w-137.5">

                                                <LogoBox
                                                      brand={brands[activeIndices[0]]}
                                                      delay={0}
                                                      bgColor="bg-white/80"
                                                      className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 lg:w-48 lg:h-48 z-30 lg:-rotate-3 shadow-lg"
                                                />

                                                <LogoBox
                                                      brand={brands[activeIndices[1]]}
                                                      delay={1.5}
                                                      bgColor="bg-white/60"
                                                      className="top-[5%] right-[5%] lg:top-[10%] lg:right-[10%] w-20 h-20 lg:w-36 lg:h-36 z-20 rotate-6"
                                                />

                                                <LogoBox
                                                      brand={brands[activeIndices[2]]}
                                                      delay={0.5}
                                                      bgColor="bg-white/70"
                                                      className="bottom-[5%] right-[10%] lg:bottom-[15%] lg:right-[5%] w-24 h-24 lg:w-48 lg:h-48 z-20 lg:-rotate-5"
                                                />

                                                <LogoBox
                                                      brand={brands[activeIndices[3]]}
                                                      delay={2}
                                                      bgColor="bg-white/60"
                                                      className="top-[10%] left-[5%] lg:top-[10%] lg:left-[5%] w-20 h-20 lg:w-32 lg:h-32 z-10 lg:rotate-4"
                                                />

                                                <LogoBox
                                                      brand={brands[activeIndices[4]]}
                                                      delay={1}
                                                      bgColor="bg-white/50"
                                                      className="bottom-[10%] left-[5%] lg:bottom-[15%] lg:left-[10%] w-16 h-16 lg:w-24 lg:h-24 z-20 -rotate-6"
                                                />
                                          </div>
                                    </div>

                                    <div className="lg:hidden mt-8 z-30 relative">
                                          <ExpandButton isExpanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />
                                    </div>

                              </div>
                        </div>

                        <AnimatePresence>
                              {isExpanded && (
                                    <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{ height: "auto", opacity: 1 }}
                                          exit={{ height: 0, opacity: 0 }}
                                          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                          className="overflow-hidden mt-12"
                                    >
                                          <div className="pt-10 pb-4 border-t border-gray-200/50 relative">

                                                <div className="absolute left-0 top-0 bottom-0 w-24 bg-linear-to-r from-[#FAFAFA] to-transparent z-10 pointer-events-none" />
                                                <div className="absolute right-0 top-0 bottom-0 w-24 bg-linear-to-l from-[#FAFAFA] to-transparent z-10 pointer-events-none" />

                                                <div className="flex flex-col gap-6">
                                                      <div className="relative w-full flex overflow-hidden">
                                                            <motion.div
                                                                  className="flex items-center gap-6"
                                                                  animate={{ x: ["0%", "-50%"] }}
                                                                  transition={{ duration: 40, ease: "linear", repeat: Infinity }}
                                                            >
                                                                  {[...firstRow, ...firstRow].map((brand, i) => {
                                                                        if (brand?.brandLogo?.filename_disk) {
                                                                              return (
                                                                                    <div key={`${brand.brandLogo.filename_disk}-top-${i}`} className="relative w-32 h-20 md:w-40 md:h-24 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center shrink-0 p-4">
                                                                                          <div className="relative w-full h-full">
                                                                                                <Image
                                                                                                      src={`https://api.gynoclub.com.tr/assets/${brand.brandLogo.filename_disk}`}
                                                                                                      alt={brand.brandLogo.title}
                                                                                                      fill
                                                                                                      className="object-contain"
                                                                                                />
                                                                                          </div>
                                                                                    </div>
                                                                              )
                                                                        }
                                                                  })}
                                                            </motion.div>
                                                      </div>

                                                      <div className="relative w-full flex overflow-hidden">
                                                            <motion.div
                                                                  className="flex items-center gap-6"
                                                                  animate={{ x: ["-50%", "0%"] }}
                                                                  transition={{ duration: 45, ease: "linear", repeat: Infinity }}
                                                            >
                                                                  {[...secondRow, ...secondRow].map((brand, i) => {
                                                                        if (brand?.brandLogo?.filename_disk) {
                                                                              return (
                                                                                    <div key={`${brand.brandLogo.filename_disk}-bottom-${i}`} className="relative w-32 h-20 md:w-40 md:h-24 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center shrink-0 p-4">
                                                                                          <div className="relative w-full h-full">
                                                                                                <Image
                                                                                                      src={`https://api.gynoclub.com.tr/assets/${brand.brandLogo.filename_disk}`}
                                                                                                      alt={brand.brandLogo.title}
                                                                                                      fill
                                                                                                      className="object-contain"
                                                                                                />
                                                                                          </div>
                                                                                    </div>
                                                                              )
                                                                        }
                                                                  })}
                                                            </motion.div>
                                                      </div>
                                                </div>
                                          </div>
                                    </motion.div>
                              )}
                        </AnimatePresence>
                  </div>
            </section>
      );
};

export default BrandIntegrationSection;